import { useCallback, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  BulkPaymentTransferFieldsFragment,
  BulkPaymentTransferFieldsFragmentDoc,
  PaymentPaymentRequestsNestedDocument,
  RevokeTransfersToPaymentDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface SiblingTransfer {
  id: number
  beneficiaryName: string
  amountCents: number
  currency: string
  [key: string]: unknown
}

export interface RemoveConfirmationData {
  paymentRequestLabel: string
  paymentId: number
  transfers: SiblingTransfer[]
  transferIds: number[]
}

export const useRemoveTransferFromPayment = () => {
  const client = useApolloClient()
  const [isRemoving, setIsRemoving] = useState(false)
  const [removeConfirmation, setRemoveConfirmation] = useState<RemoveConfirmationData | null>(null)

  const [revokeTransfers] = useMutation(RevokeTransfersToPaymentDocument, {
    extractorFn: (data) => data.bankingMutations.revokeTransfersBankingPayment,
    update: (cache) => {
      cache.evict({ fieldName: 'paymentsConnection' })
      cache.evict({ fieldName: 'payment' })
      cache.evict({ fieldName: 'transfersConnection' })
      cache.gc()
    },
  })

  const executeRevoke = useCallback(
    async (paymentId: number, transferIds: number[]) => {
      setIsRemoving(true)
      try {
        const result = await revokeTransfers({
          variables: { paymentId, transferIds },
        })

        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('banking.bulkPayments.detail.removeError'),
          })
        })

        if (handled.success) {
          await client.refetchQueries({ include: 'active' })
          app.setFlash({
            type: 'correct',
            message: i18n.t('banking.bulkPayments.detail.removeSuccess'),
          })
        }

        return handled
      } finally {
        setIsRemoving(false)
      }
    },
    [revokeTransfers, client]
  )

  const handleRemoveTransfer = useCallback(
    async (
      transfer: BulkPaymentTransferFieldsFragment,
      paymentId: number,
      paymentExecutionIds: number[]
    ) => {
      const paymentRequest = transfer.paymentRequestItem?.paymentRequest
      if (!paymentRequest) return

      // Fetch all transfers for this payment, paginating to ensure none are missed
      let after: string | undefined
      let hasNextPage = true
      const allNodes: Array<{
        id: number
        amountCents: number
        currency: string
        paymentRequestItem?: { paymentRequest?: { id: number } } | null
      }> = []

      while (hasNextPage) {
        const { data } = await client.query({
          query: PaymentPaymentRequestsNestedDocument,
          variables: {
            paymentExecutionIds,
            first: 100,
            after,
          },
          fetchPolicy: 'network-only',
        })

        const edges = data.banking.transfersConnection.edges ?? []
        for (const edge of edges) {
          if (edge?.node) allNodes.push(edge.node)
        }

        hasNextPage = data.banking.transfersConnection.pageInfo.hasNextPage
        after = data.banking.transfersConnection.pageInfo.endCursor ?? undefined
      }

      const allTransfers = allNodes

      // Find all transfers belonging to the same payment request
      const siblingTransfers = allTransfers.filter(
        (t) => t.paymentRequestItem?.paymentRequest?.id === paymentRequest.id
      )

      const transferIds = siblingTransfers.map((t) => t.id)

      // Read beneficiary names from Apollo cache (populated by the data collection table)
      const transfers: SiblingTransfer[] = siblingTransfers.map((t) => {
        const cached = client.cache.readFragment<BulkPaymentTransferFieldsFragment>({
          id: client.cache.identify({ __typename: 'BankingTransfer', id: t.id }),
          fragment: BulkPaymentTransferFieldsFragmentDoc,
        })

        return {
          id: t.id,
          beneficiaryName: cached?.beneficiaryName ?? '-',
          amountCents: t.amountCents,
          currency: t.currency,
        }
      })

      setRemoveConfirmation({
        paymentRequestLabel: paymentRequest.label,
        paymentId,
        transfers,
        transferIds,
      })
    },
    [client, executeRevoke]
  )

  const confirmRemove = useCallback(async () => {
    if (!removeConfirmation) return
    await executeRevoke(removeConfirmation.paymentId, removeConfirmation.transferIds)
    setRemoveConfirmation(null)
  }, [removeConfirmation, executeRevoke])

  const cancelRemove = useCallback(() => {
    if (!isRemoving) {
      setRemoveConfirmation(null)
    }
  }, [isRemoving])

  return {
    handleRemoveTransfer,
    removeConfirmation,
    confirmRemove,
    cancelRemove,
    isRemoving,
  }
}
